import { type KeyboardEvent, type PointerEvent, type RefObject, useRef, useState } from "react";
import { cn } from "@/shared/cn";

export function Splitter({
  label,
  targetRef,
  containerRef,
  width,
  onChange,
  onReset,
}: {
  label: string;
  targetRef: RefObject<HTMLElement | null>;
  containerRef: RefObject<HTMLElement | null>;
  width: number | null;
  onChange: (width: number, done: boolean) => void;
  onReset: () => void;
}) {
  const drag = useRef<{ x: number; width: number; last: number | null } | null>(null);
  const [dragging, setDragging] = useState(false);

  function clamp(next: number) {
    const container = containerRef.current?.getBoundingClientRect().width ?? 0;
    return clampConversationWidth(next, container);
  }

  function onPointerDown(event: PointerEvent<HTMLDivElement>) {
    const target = targetRef.current;
    if (!target) return;
    const start = width === null ? target.getBoundingClientRect().width : clamp(width);
    drag.current = { x: event.clientX, width: start, last: null };
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setDragging(true);
    document.body.classList.add("resizing");
    event.preventDefault();
  }

  function onPointerMove(event: PointerEvent<HTMLDivElement>) {
    if (!drag.current) return;
    const next = clamp(drag.current.width + event.clientX - drag.current.x);
    drag.current.last = next;
    onChange(next, false);
  }

  function onPointerEnd() {
    if (!drag.current) return;
    const { last } = drag.current;
    drag.current = null;
    setDragging(false);
    document.body.classList.remove("resizing");
    if (last !== null) onChange(last, true);
  }

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    event.preventDefault();
    const current = width ?? targetRef.current?.getBoundingClientRect().width ?? 0;
    onChange(clamp(current + (event.key === "ArrowRight" ? 24 : -24)), true);
  }

  return (
    // biome-ignore lint/a11y/useSemanticElements: a focusable vertical separator is the ARIA pattern for a pointer-and-keyboard resize handle; <hr> cannot take focus.
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-valuenow={width ?? undefined}
      tabIndex={0}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerEnd}
      onPointerCancel={onPointerEnd}
      onDoubleClick={onReset}
      onKeyDown={onKeyDown}
      className={cn(
        "group relative z-[6] -mx-[6px] w-[12px] shrink-0 cursor-col-resize touch-none focus-visible:outline-none",
      )}
    >
      <span
        aria-hidden
        className={cn(
          "absolute top-1/2 left-1/2 -mt-[20px] -ml-[2px] h-[40px] w-[4px] rounded-full transition-[background] duration-150",
          "group-focus-visible:outline-2 group-focus-visible:outline-offset-3 group-focus-visible:outline-(--brand)",
          dragging
            ? "bg-[color-mix(in_srgb,var(--ink)_45%,transparent)]"
            : "bg-[color-mix(in_srgb,var(--ink)_22%,transparent)] group-hover:bg-[color-mix(in_srgb,var(--ink)_45%,transparent)]",
        )}
      />
    </div>
  );
}

function clampConversationWidth(width: number, container: number): number {
  const max = Math.max(CONVERSATION_MIN_MAX, container * CONVERSATION_MAX_SHARE);
  return Math.round(Math.min(max, Math.max(CONVERSATION_MIN_WIDTH, width)));
}

const CONVERSATION_MIN_WIDTH = 280;
const CONVERSATION_MIN_MAX = 320;
const CONVERSATION_MAX_SHARE = 0.6;
